import React, { useState, useEffect, useRef } from 'react';
import { ChevronDownIcon, PlusIcon, BuildingOffice2Icon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { suppliersApi, Supplier, SupplierType, SupplierStatus } from '../api/suppliers';

interface SupplierSelectorProps {
  value?: string;
  onChange: (supplierId: string, supplier?: Supplier) => void;
  onSupplierContactChange?: (contact: string) => void;
  filterByType?: SupplierType;
  placeholder?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  showCreateButton?: boolean;
  onCreateSupplier?: () => void;
}

export default function SupplierSelector({
  value,
  onChange,
  onSupplierContactChange,
  filterByType,
  placeholder = 'Select a supplier...',
  className = '',
  required = false,
  disabled = false,
  showCreateButton = true,
  onCreateSupplier
}: SupplierSelectorProps) {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadSuppliers();
  }, [filterByType]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  const loadSuppliers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await suppliersApi.getSuppliers({
        type: filterByType,
        status: SupplierStatus.ACTIVE,
        limit: 100
      });
      
      const suppliers = response.suppliers || [];
      setSuppliers(suppliers);
      
      // If no suppliers found with filter, try without filter as fallback
      if (suppliers.length === 0 && filterByType) {
        const fallbackResponse = await suppliersApi.getSuppliers({
          status: SupplierStatus.ACTIVE,
          limit: 100
        });
        
        if (fallbackResponse.suppliers && fallbackResponse.suppliers.length > 0) {
          setSuppliers(fallbackResponse.suppliers);
        }
      }
      
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load suppliers';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const selectedSupplier = suppliers.find(s => s.id === value);

  const filteredSuppliers = suppliers.filter(supplier => {
    if (!searchTerm.trim()) return true;
    
    const searchLower = searchTerm.toLowerCase().trim();
    const nameMatch = supplier.name?.toLowerCase().includes(searchLower);
    const codeMatch = supplier.code?.toLowerCase().includes(searchLower);
    const contactMatch = supplier.contactPerson?.toLowerCase().includes(searchLower);
    const emailMatch = supplier.email?.toLowerCase().includes(searchLower);
    
    return nameMatch || codeMatch || contactMatch || emailMatch;
  });


  const handleSupplierSelect = (supplier: Supplier) => {
    onChange(supplier.id, supplier);
    if (onSupplierContactChange && supplier.phone) {
      onSupplierContactChange(supplier.phone);
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleCreateSupplier = () => {
    setIsOpen(false);
    setSearchTerm('');
    if (onCreateSupplier) {
      onCreateSupplier();
    }
  };

  const handleToggleDropdown = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm('');
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
  };

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        <button
          type="button"
          onClick={handleToggleDropdown}
          disabled={disabled}
          className={`
            relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-left shadow-sm 
            focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 sm:text-sm
            ${disabled ? 'bg-slate-50/80 text-slate-500 cursor-not-allowed' : 'hover:border-gray-400'}
            ${required && !value ? 'border-red-300' : ''}
          `}
        >
          <span className="flex items-center">
            <BuildingOffice2Icon className="h-5 w-5 text-slate-400 mr-2" />
            <span className="block truncate">
              {selectedSupplier ? (
                <span>
                  <span className="font-medium">{selectedSupplier.name}</span>
                  {selectedSupplier.code && (
                    <span className="text-slate-500 ml-2">({selectedSupplier.code})</span>
                  )}
                </span>
              ) : (
                <span className="text-slate-500">{placeholder}</span>
              )}
            </span>
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDownIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
          </span>
        </button>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-full rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5">
            {/* Search Input */}
            <div className="p-3 border-b border-gray-200">
              <div className="flex items-center w-full rounded-md border border-gray-300 bg-white focus-within:border-cyan-500 focus-within:ring-1 focus-within:ring-cyan-500">
                <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
                  <MagnifyingGlassIcon className="h-4 w-4" />
                </span>
                <input
                  ref={searchInputRef}
                  type="text"
                  className="flex-1 min-w-0 py-2 pr-3 border-0 bg-transparent text-sm focus:outline-none focus:ring-0"
                  placeholder="Search suppliers by name, code, or contact..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
            </div>

            {/* Suppliers List */}
            <div className="max-h-60 overflow-auto">
              {loading ? (
                <div className="px-3 py-4 text-sm text-slate-500 text-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-500 mx-auto mb-2"></div>
                  Loading suppliers...
                </div>
              ) : error ? (
                <div className="px-3 py-4 text-sm text-red-600 text-center">{error}</div>
              ) : filteredSuppliers.length === 0 ? (
                <div className="px-3 py-4 text-sm text-slate-500 text-center">
                  {searchTerm ? (
                    <>
                      No suppliers found matching "<strong>{searchTerm}</strong>"
                      <br />
                      <span className="text-xs">Try a different search term</span>
                    </>
                  ) : suppliers.length === 0 ? (
                    <div className="space-y-2">
                      <div>No suppliers available</div>
                      {filterByType && (
                        <div className="text-xs text-amber-600">
                          No suppliers found with type "{filterByType.toLowerCase()}"
                        </div>
                      )}
                      <div className="text-xs text-cyan-600">
                        {showCreateButton ? 'Click "Create New Supplier" below to add one' : 'Contact admin to add suppliers'}
                      </div>
                    </div>
                  ) : (
                    'No suppliers match current filters.'
                  )}
                </div>
              ) : (
                <div className="py-1">
                  {filteredSuppliers.map((supplier) => (
                    <button
                      key={supplier.id}
                      type="button"
                      onClick={() => handleSupplierSelect(supplier)}
                      className={`
                        w-full text-left px-3 py-3 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors
                        ${supplier.id === value ? 'bg-cyan-50 text-indigo-900' : 'text-slate-900'}
                      `}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{supplier.name}</div>
                          <div className="text-slate-500 text-xs mt-1 space-y-1">
                            {supplier.code && (
                              <div>Code: <span className="font-mono">{supplier.code}</span></div>
                            )}
                            {supplier.contactPerson && (
                              <div>Contact: {supplier.contactPerson}</div>
                            )}
                            {supplier.phone && (
                              <div>Phone: {supplier.phone}</div>
                            )}
                            {supplier.email && (
                              <div>Email: {supplier.email}</div>
                            )}
                          </div>
                        </div>
                        <div className="ml-2 flex-shrink-0">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-800 capitalize">
                            {supplier.type.toLowerCase()}
                          </span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Create New Supplier Button */}
            {showCreateButton && (
              <div className="border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCreateSupplier}
                  className="w-full text-left px-3 py-3 text-sm text-cyan-600 hover:bg-cyan-50 focus:bg-cyan-50 focus:outline-none flex items-center transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Create new supplier
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
} 